import React, { useState } from 'react';
import { X, Paperclip, Send, Loader2, FileText, Image as ImageIcon } from 'lucide-react';

const iconFor = (mime) => {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return { icon: ImageIcon, color: 'text-sky-600' };
  if (m.includes('pdf')) return { icon: FileText, color: 'text-red-600' };
  if (m.includes('sheet') || m.includes('excel') || m.includes('csv')) return { icon: FileText, color: 'text-emerald-600' };
  if (m.includes('word') || m.includes('document')) return { icon: FileText, color: 'text-blue-600' };
  return { icon: Paperclip, color: 'text-slate-500' };
};

const fmtSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Mini-dialog de contexto antes de enviar um anexo.
 *
 * Props:
 *   file: File (preview)
 *   tipos: [{ value, label_pt }]
 *   queueIndex / queueLength: para multiples arquivos (ex: 2 de 3)
 *   onConfirm({ document_type, description }) — envia
 *   onCancel — cancela esse arquivo (e os proximos da fila)
 */
const UploadDialog = ({ file, tipos, queueIndex = 0, queueLength = 1, onConfirm, onCancel, sending }) => {
  const [docType, setDocType] = useState('');
  const [desc, setDesc] = useState('');

  if (!file) return null;
  const { icon: Icon, color } = iconFor(file.type);
  const multi = queueLength > 1;

  const handleConfirm = () => {
    onConfirm({
      document_type: docType || null,
      description: desc.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10005] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Paperclip size={18} className="text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-800">Anexar arquivo à oferta</h3>
            {multi && (
              <span className="text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                {queueIndex + 1} de {queueLength}
              </span>
            )}
          </div>
          <button
            onClick={onCancel}
            disabled={sending}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* Preview do arquivo */}
          <div className="flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded">
            <Icon size={22} className={`${color} flex-shrink-0`} />
            <div className="min-w-0 flex-grow">
              <div className="text-sm font-medium text-slate-800 truncate" title={file.name}>
                {file.name}
              </div>
              <div className="text-[11px] text-slate-500">
                {fmtSize(file.size)} · {file.type || 'tipo desconhecido'}
              </div>
            </div>
          </div>

          {/* Tipo de documento */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Tipo de documento <span className="text-slate-400 font-normal">(opcional)</span>
            </label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              disabled={sending}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50"
            >
              <option value="">— selecione —</option>
              {tipos.map(t => (
                <option key={t.value} value={t.value}>{t.label_pt}</option>
              ))}
            </select>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Descrição <span className="text-slate-400 font-normal">(opcional — ajuda a IA a entender)</span>
            </label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, window.innerHeight * 0.5) + 'px';
              }}
              disabled={sending}
              rows={3}
              placeholder="Ex: Proposta de R$45M para lote 7.885 — versão nov/2025"
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 resize-none overflow-y-auto"
              style={{ maxHeight: '50vh' }}
            />
          </div>
        </div>

        <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={sending}
            className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded disabled:opacity-50"
          >
            Cancelar {multi ? 'todos' : ''}
          </button>
          <button
            onClick={handleConfirm}
            disabled={sending}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDialog;
